import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { siteConfig } from "@/data/siteConfig";

const LangSwitch = ({ lang, onSwitch, mobile = false }) => (
  <div
    className={`flex items-center whitespace-nowrap ${mobile ? "text-sm" : "text-xs"} tracking-[0.12em] font-medium`}
    role="group"
    aria-label="Idioma / Language"
    data-testid={mobile ? "mobile-language-toggle" : "language-toggle"}
  >
    <button
      onClick={() => onSwitch("es")}
      data-testid={mobile ? "mobile-lang-es-button" : "lang-es-button"}
      aria-current={lang === "es" ? "true" : undefined}
      className={`transition-colors ${lang === "es" ? "text-ser-purple" : "text-navy/50 hover:text-navy"}`}
    >
      ESPAÑOL
    </button>
    <span className="mx-2 text-navy/30" aria-hidden="true">|</span>
    <button
      onClick={() => onSwitch("en")}
      data-testid={mobile ? "mobile-lang-en-button" : "lang-en-button"}
      aria-current={lang === "en" ? "true" : undefined}
      className={`transition-colors ${lang === "en" ? "text-ser-purple" : "text-navy/50 hover:text-navy"}`}
    >
      ENGLISH
    </button>
  </div>
);

export const Header = () => {
  const { lang, t } = useLang();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const links = [
    { to: `/${lang}`, label: t.nav.home, end: true, id: "home" },
    { to: `/${lang}/about`, label: t.nav.about, id: "about" },
    { to: `/${lang}/services`, label: t.nav.services, id: "services" },
    { to: `/${lang}/team`, label: t.nav.team, id: "team" },
    { to: `/${lang}/el-salvador-ee-uu`, label: t.nav.diaspora, id: "diaspora" },
    { to: `/${lang}/contact`, label: t.nav.contact, id: "contact" },
  ];

  const switchLang = (target) => {
    if (target === lang) return;
    const rest = location.pathname.replace(/^\/(es|en)/, "") || "";
    navigate(`/${target}${rest}`);
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-bone/90 backdrop-blur-md border-b border-navy/10">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:bg-navy focus:text-white focus:px-4 focus:py-2"
        data-testid="skip-to-content-link"
      >
        {t.skipToContent}
      </a>
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
        <Link to={`/${lang}`} className="flex items-center gap-3 shrink-0" data-testid="header-logo-link" aria-label={t.brandName}>
          <img src={siteConfig.logoMark} alt={t.brandName} className="h-12 w-auto" />
        </Link>
        <div
          className="hidden xl:flex flex-col justify-center border-l border-navy/15 pl-3 ml-1 shrink-0"
          data-testid="header-brand-tagline"
        >
          {t.brandTagline.split(" - ").map((w) => (
            <span key={w} className="text-[9px] uppercase tracking-[0.18em] leading-[1.35] text-navy/50 font-medium">
              {w}
            </span>
          ))}
        </div>

        <nav className="hidden lg:flex items-center gap-6 ml-8" aria-label="Principal">
          {links.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.id}-link`}
              className={({ isActive }) =>
                `text-sm tracking-wide whitespace-nowrap transition-colors ${
                  isActive ? "text-ser-purple font-medium" : "text-navy/70 hover:text-navy"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5 ml-auto">
          <LangSwitch lang={lang} onSwitch={switchLang} />
        </div>

        <button
          className="lg:hidden p-2 text-navy"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={open}
          data-testid="mobile-menu-button"
        >
          {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="lg:hidden overflow-hidden border-t border-navy/10 bg-bone"
            aria-label="Menú móvil"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  end={l.end}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.id}-link`}
                  className={({ isActive }) =>
                    `text-base ${isActive ? "text-ser-purple font-medium" : "text-navy/80"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="flex items-center justify-between pt-4 border-t border-navy/10">
                <LangSwitch lang={lang} onSwitch={switchLang} mobile />
                <Link
                  to={`/${lang}/contact`}
                  onClick={() => setOpen(false)}
                  data-testid="mobile-cta-button"
                  className="bg-navy text-white text-sm font-medium px-6 py-2.5"
                >
                  {t.cta}
                </Link>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
